import axiosInstance from '../../utils/api';
import { sanitizeAttribution } from '../../utils/utmAttribution';
export { default } from './index.jsx';
export { default as DoshaQuiz } from './index.jsx';

const getPlayersApiPath = (endpoint = '') => {
  const base = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '');
  const cleanEndpoint = endpoint.replace(/^\//, '');

  if (base.endsWith('/api/v1/players')) {
    return cleanEndpoint;
  }

  return cleanEndpoint
    ? `/api/v1/players/${cleanEndpoint}`
    : '/api/v1/players';
};


export const getQuiz = async (userId, language = null) => {
  try {
    const params = {
      user_id: userId
    };
    
    // Add language parameter if provided
    if (language) {
      params.language = language;
    }
    
    const response = await axiosInstance.get(getPlayersApiPath('get_quiz'), {
      params
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDoshaSuggestedProducts = async (token, dosha) => {
  try {
    const response = await axiosInstance.get(
      `${getPlayersApiPath('dosha_suggested_products')}?dosha=${dosha}`, {
      headers: {
        Authorization: `Bearer ${token}`, 
        "Content-Type": "application/json",
        Accept: 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDoshaSuggestedBlogs = async (token, dosha) => {
  try {
    const response = await axiosInstance.get(
      `${getPlayersApiPath('blog_posts')}?dosha=${dosha}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json"
        }
      }
    );
    return response.data;
    
  } catch (error) {
    throw error;
  }
};

export const markAsDoshaQuizCompleted = async (
  token,
  finalData,
  playerId,
  utmAttribution = {},
  quizName = null
) => {
  try {
    const cleanedAttribution = sanitizeAttribution(utmAttribution);
    const requestData = {
      result: finalData,
      player_id: playerId,
      ...(quizName && { quiz_name: quizName }),
      ...(Object.keys(cleanedAttribution).length > 0 && {
        attribution: cleanedAttribution,
        ...cleanedAttribution,
      }),
    };
    
    const response = await axiosInstance.post(getPlayersApiPath('mark_as_completed'), requestData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json; charset=utf-8",
        Accept: "application/json"
      }
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const ASYNC_STATUS_POLL_INTERVAL_MS = 5000;
const ASYNC_STATUS_POLL_TIMEOUT_MS = 90000;
const COMPLETED_ASYNC_STATUSES = new Set([
  'completed',
  'complete',
  'success',
  'succeeded',
  'done',
  'finished',
  'ready',
]);
const FAILED_ASYNC_STATUSES = new Set([
  'failed',
  'error',
  'errored',
  'cancelled',
  'canceled',
]);
const getAuthHeaders = (token, extra = {}) => ({
  Authorization: `Bearer ${token}`,
  Accept: 'application/json',
  ...extra,
});

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timer = setTimeout(resolve, ms);
    if (!signal) return;

    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });

const getAsyncJobStatus = (payload) => {
  if (!payload || typeof payload !== 'object') return '';

  return String(
    payload.status ||
      payload.state ||
      payload.job_status ||
      payload.generation_status ||
      payload.data?.status ||
      payload.result?.status ||
      ''
  ).toLowerCase();
};

const looksLikeDoshaResult = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false;
  }

  return Boolean(
    payload.dominant_dosha_section ||
      payload.balance_understanding_section ||
      payload.common_traits_section ||
      payload.ama_section ||
      payload.level_advice_content ||
      payload.dominant_dosha
  );
};

const normalizeObjectKeys = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }
  const normalized = {};
  for (const [key, value] of Object.entries(obj)) {
    const normalizedKey = key.toLowerCase();
    normalized[normalizedKey] =
      typeof value === 'object' && value !== null && !Array.isArray(value)
        ? normalizeObjectKeys(value)
        : value;
  }
  return normalized;
};

const splitSseDataEvents = (text) =>
  String(text)
    .split(/(?=data:\s*)/)
    .map((part) => part.trim())
    .filter((part) => part.startsWith('data:'));

const parseSseChunk = (chunk) => {
  if (!chunk) return null;
  if (typeof chunk === 'object') return chunk;

  let cleaned = String(chunk)
    .replace(/^(data:\s*)+/g, '')
    .trim();
  if (!cleaned) return null;

  const jsonStart = cleaned.indexOf('{');
  const jsonEnd = cleaned.lastIndexOf('}');
  if (jsonStart !== -1 && jsonEnd > jsonStart) {
    cleaned = cleaned.slice(jsonStart, jsonEnd + 1);
  }

  try {
    return JSON.parse(cleaned);
  } catch (error) {
    console.error('Failed to parse dosha result chunk:', error);
    return null;
  }
};

const getResultChunks = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const result = payload.result || payload.data?.result || payload;
  const chunks = result?.chunks || payload.chunks || payload.data?.chunks;

  if (Array.isArray(chunks) && chunks.length > 0) {
    return chunks;
  }

  const content = result?.content || payload.content;
  if (typeof content === 'string' && content.includes('component_name')) {
    const events = splitSseDataEvents(content);
    return events.length ? events : null;
  }

  return null;
};

export const parseDoshaResultChunks = (chunks) => {
  if (!Array.isArray(chunks) || !chunks.length) return {};

  return chunks.reduce((parsed, chunk) => {
    const resultData = parseSseChunk(chunk);
    if (!resultData) return parsed;

    const normalizedKey = resultData.component_name
      ?.toLowerCase()
      .replace(/-/g, '_') || resultData.component_name;
    if (!normalizedKey) return parsed;

    let componentValue = resultData.value || resultData.Value;
    if (componentValue && typeof componentValue === 'object' && !Array.isArray(componentValue)) {
      componentValue = normalizeObjectKeys(componentValue);
    }

    parsed[normalizedKey] = componentValue;
    return parsed;
  }, {});
};

export const parseAsyncDoshaStatusPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return payload || {};
  if (looksLikeDoshaResult(payload)) return payload;

  const chunks = getResultChunks(payload);
  if (chunks) {
    return parseDoshaResultChunks(chunks);
  }

  return unwrapAsyncPayload(payload);
};

export const unwrapAsyncPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (looksLikeDoshaResult(payload)) return payload;

  const chunks = getResultChunks(payload);
  if (chunks) {
    return parseDoshaResultChunks(chunks);
  }

  const nested =
    payload.result ??
    payload.data ??
    payload.response ??
    payload.content ??
    payload.output ??
    payload.payload;

  if (nested && typeof nested === 'object' && !Array.isArray(nested) && nested !== payload) {
    return unwrapAsyncPayload(nested);
  }

  return payload;
};

const resolvePlayerId = (quizData, generateResponse) =>
  quizData?.player_id ||
  generateResponse?.player_id ||
  generateResponse?.player?.id ||
  generateResponse?.data?.player_id ||
  generateResponse?.data?.player?.id;

const hasCompletedResult = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  if (COMPLETED_ASYNC_STATUSES.has(getAsyncJobStatus(payload))) return true;
  if (getResultChunks(payload)) return true;
  return looksLikeDoshaResult(unwrapAsyncPayload(payload));
};

const shouldPollAsyncStatus = (generateResponse, playerId) => {
  if (!playerId) return false;
  if (FAILED_ASYNC_STATUSES.has(getAsyncJobStatus(generateResponse))) return false;
  if (hasCompletedResult(generateResponse)) return false;
  return true;
};

export const asyncGenerateDoshaQuiz = async (quizData, token = null) => {
  const response = await axiosInstance.post(
    getPlayersApiPath('async_generate'),
    quizData,
    {
      headers: getAuthHeaders(token, { 'Content-Type': 'application/json' }),
    }
  );
  return response.data;
};

export const getDoshaQuizAsyncStatus = async (playerId, token = null) => {
  const response = await axiosInstance.get(
    getPlayersApiPath(`${playerId}/async_status`),
    {
      headers: getAuthHeaders(token),
    }
  );
  return response.data;
};

export const pollDoshaQuizAsyncStatus = async (
  playerId,
  token = null,
  { intervalMs = ASYNC_STATUS_POLL_INTERVAL_MS, timeoutMs = ASYNC_STATUS_POLL_TIMEOUT_MS, signal } = {}
) => {
  if (!playerId) {
    throw new Error('player_id is required to poll async quiz status');
  }

  const startedAt = Date.now();
  let lastPayload = null;

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      lastPayload = await getDoshaQuizAsyncStatus(playerId, token);
      const status = getAsyncJobStatus(lastPayload);

      if (
        FAILED_ASYNC_STATUSES.has(status) ||
        (lastPayload?.error_message && !getResultChunks(lastPayload))
      ) {
        const failedError = new Error(
          lastPayload?.error_message ||
            lastPayload?.error ||
            lastPayload?.message ||
            lastPayload?.data?.error ||
            'Dosha quiz generation failed'
        );
        failedError.isAsyncJobFailure = true;
        throw failedError;
      }

      if (hasCompletedResult(lastPayload)) {
        return parseAsyncDoshaStatusPayload(lastPayload);
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.isAsyncJobFailure) {
        throw error;
      }

      const statusCode = error.response?.status;
      const canRetry =
        !statusCode ||
        statusCode === 404 ||
        statusCode === 408 ||
        statusCode === 425 ||
        statusCode === 429 ||
        statusCode >= 500;

      if (!canRetry) {
        throw error;
      }
    }

    if (Date.now() - startedAt >= timeoutMs) {
      if (lastPayload) return parseAsyncDoshaStatusPayload(lastPayload);
      throw new Error('Timed out waiting for dosha quiz result');
    }

    await sleep(intervalMs, signal);
  }
};

const runGenerateAndPollCycle = async (quizData, token = null, signal) => {
  const generateResponse = await asyncGenerateDoshaQuiz(quizData, token);
  const playerId = resolvePlayerId(quizData, generateResponse);
  const generateStatus = getAsyncJobStatus(generateResponse);

  if (FAILED_ASYNC_STATUSES.has(generateStatus)) {
    const failedError = new Error(
      generateResponse?.error ||
        generateResponse?.message ||
        generateResponse?.data?.error ||
        'Dosha quiz generation failed'
    );
    failedError.isAsyncJobFailure = true;
    throw failedError;
  }

  if (!shouldPollAsyncStatus(generateResponse, playerId)) {
    return parseAsyncDoshaStatusPayload(generateResponse);
  }

  return pollDoshaQuizAsyncStatus(playerId, token, { signal });
};

export const generateDoshaQuizAsyncResult = async (
  quizData,
  token = null,
  { signal } = {}
) => {
  try {
    return await runGenerateAndPollCycle(quizData, token, signal);
  } catch (error) {
    if (error?.name === 'AbortError' || !error?.isAsyncJobFailure) {
      throw error;
    }

    return runGenerateAndPollCycle(quizData, token, signal);
  }
};

export const submitDoshaQuiz = async (quizData, token = null) => {
  const response = await axiosInstance.post(
    getPlayersApiPath(),
    quizData,
    {
      headers: getAuthHeaders(token, { 'Content-Type': 'application/json' }),
    }
  );

  return response.data;
};

